import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { RiFileEditLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { brandPalette } from "../../../constants/brandPalette";
import { substituteSampleValues } from "../../../modules/admin-modules/whatsapp-templates/utils";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const MetaItem = ({ label, value }) => (
  <Box>
    <Text
      fontSize="11px"
      fontWeight="700"
      letterSpacing="0.04em"
      textTransform="uppercase"
      color="var(--text-muted)"
      mb={1}
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="600" color="var(--text-primary)" noOfLines={1}>
      {value || "—"}
    </Text>
  </Box>
);

const MessagePreview = ({ bodyContent, sampleValues, displayName }) => {
  const preview = substituteSampleValues(bodyContent, sampleValues || {});

  return (
    <Box
      bg="#0b141a"
      borderRadius="14px"
      p={3}
      h="100%"
      minH="200px"
      backgroundImage="radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)"
      backgroundSize="16px 16px"
    >
      <Text color="rgba(255,255,255,0.45)" fontSize="11px" mb={2} noOfLines={1}>
        Preview{displayName ? ` · ${displayName}` : ""}
      </Text>
      <Box
        bg="#202c33"
        color="#e9edef"
        px={3}
        py={2.5}
        borderRadius="8px"
        borderTopLeftRadius="0"
        whiteSpace="pre-wrap"
        fontSize="13px"
        lineHeight="1.5"
        fontFamily="'Segoe UI', Tahoma, sans-serif"
        maxH="220px"
        overflowY="auto"
      >
        {preview?.trim() ? (
          preview
        ) : (
          <Text as="span" color="rgba(233,237,239,0.4)" fontStyle="italic">
            No message body
          </Text>
        )}
      </Box>
    </Box>
  );
};

const TemplateCard = ({ template, onEdit }) => {
  return (
    <Box
      as="article"
      bg="var(--surface-raised)"
      border="1px solid"
      borderColor={C.border}
      borderRadius="18px"
      overflow="hidden"
      transition="border-color 0.15s ease, box-shadow 0.15s ease"
      _hover={{
        borderColor: "rgba(91, 13, 22, 0.35)",
        boxShadow: "0 8px 24px rgba(46, 42, 38, 0.08)",
      }}
      cursor="pointer"
      onClick={() => onEdit(template)}
    >
      <SimpleGrid columns={{ base: 1, lg: 2 }} minH={{ lg: "240px" }}>
        <Flex
          direction="column"
          justify="space-between"
          gap={5}
          p={{ base: 5, md: 6 }}
        >
          <Box>
            <Text
              fontSize="xs"
              fontWeight="700"
              color={C.secondary}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              mb={2}
              noOfLines={1}
            >
              {template.name}
            </Text>
            <Text
              fontSize={{ base: "22px", md: "26px" }}
              fontWeight="800"
              color={C.primary}
              fontFamily="'Amiri', 'Tajawal', sans-serif"
              lineHeight="1.15"
              mb={4}
            >
              {template.display_name || template.name}
            </Text>

            <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={4}>
              <MetaItem label="Language" value={template.language} />
              <MetaItem label="Category" value={template.category} />
              <MetaItem
                label="Header"
                value={template.header_type || "NONE"}
              />
            </SimpleGrid>
          </Box>

          <Button
            alignSelf="flex-start"
            leftIcon={<RiFileEditLine />}
            bg={C.primary}
            color={brandPalette.cream}
            borderRadius="full"
            px={6}
            _hover={{ bg: C.secondary }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(template);
            }}
          >
            Edit template
          </Button>
        </Flex>

        <Box p={{ base: 4, md: 5 }} bg="var(--surface-muted)">
          <MessagePreview
            bodyContent={template.body_content}
            sampleValues={template.sample_values}
            displayName={template.display_name || template.name}
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
};

const TemplatesTable = ({ searchTerm = "", filterType = "all" }) => {
  const navigate = useNavigate();
  const list = useSelector((state) => state.whatsappTemplates.list);
  const isLoading = useSelector((state) => state.whatsappTemplates.loading);

  const filtered = useMemo(() => {
    const raw = Array.isArray(list) ? list : [];
    const q = (searchTerm || "").trim().toLowerCase();

    return raw.filter((row) => {
      const name = String(row?.name ?? "").toLowerCase();
      const displayName = String(row?.display_name ?? "").toLowerCase();
      const body = String(row?.body_content ?? "").toLowerCase();
      const isScan = name.startsWith("scan_") || name.includes("scan_");

      if (filterType === "scan" && !isScan) return false;
      if (filterType === "other" && isScan) return false;
      if (!q) return true;
      return name.includes(q) || displayName.includes(q) || body.includes(q);
    });
  }, [list, searchTerm, filterType]);

  const goEdit = (row) => {
    if (!row?.name) return;
    navigate(`/dashboard/whatsapp-templates/${encodeURIComponent(row.name)}`);
  };

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      {isLoading ? (
        <VStack spacing={5} align="stretch">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} h="240px" borderRadius="18px" />
          ))}
        </VStack>
      ) : filtered.length === 0 ? (
        <Box
          bg="var(--surface-raised)"
          border="1px solid"
          borderColor={C.border}
          borderRadius="16px"
          p={8}
        >
          <Text color="var(--text-muted)">No templates match your filters.</Text>
        </Box>
      ) : (
        <VStack spacing={5} align="stretch">
          {filtered.map((template) => (
            <TemplateCard
              key={template.name}
              template={template}
              onEdit={goEdit}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default TemplatesTable;
